// flip() and rotate() on stock model-viewer APIs.
//
// Both used to come from our node_modules_patches fork of model-viewer 1.9.2,
// which added them as public methods on the element (BZL-61). The fork is gone;
// these do the same two things through the published API, so nothing has to be
// patched into the library again.
//
// The fork's behaviour, as read from its diff against stock 1.9.2 (BZL-212):
//
//   flip    adjustOrbit(-(dOffset * PI), 0, 0) per frame. adjustOrbit SUBTRACTS
//           its delta from the goal theta, so theta GROWS by PI. The first port
//           ran theta the other way (-180) and the card spun backwards.
//   rotate  spins the scene's target about Z by PI/2 per press, on the same
//           500 ms timeline, while the camera target follows the centre of the
//           card. It is animated, and it accumulates: four presses are a full
//           turn, not a return through zero. Counter-clockwise as seen from
//           the front; from the back (cos(theta) < 0) it runs the other way so
//           it still looks counter-clockwise to the person looking.
//           (`orientation` is "roll pitch yaw" and roll is the FIRST term. The
//           first port wrote the third, which is a turn about the vertical
//           axis: the card went edge-on instead of spinning in its plane.)
//   timing  both use rotate_flip = timeline(0, [{2 frames: 0}, {5: 1}, {2: 1}]):
//           hold, easeInOutQuad ramp, hold, over 500 ms.
//
// Re-pressing while a turn is still running does nothing, as in the fork (it
// set the flag the running animation was already holding).

const DURATION_MS = 500;

// The fork's rotate_flip timeline, from stock model-viewer's
// utilities/animation.js: a 2-frame hold at 0, a 5-frame ease to 1, then a
// 2-frame hold at 1.
const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
const FRAMES = [2, 5, 2];
const TOTAL = FRAMES.reduce((a, b) => a + b, 0);
export function turnProfile(p) {
  const t = Math.min(1, Math.max(0, p)) * TOTAL;
  if (t <= FRAMES[0]) return 0;
  if (t <= FRAMES[0] + FRAMES[1]) return easeInOutQuad((t - FRAMES[0]) / FRAMES[1]);
  return 1;
}

// model-viewer 4.4.0 calls arRenderer.onUpdateScene() from every change to
// `orientation` made after the model has loaded, and that call builds an XR
// menu on a presentedScene that only exists while presenting: outside AR it
// throws "Cannot read properties of null (reading 'add')" from inside the
// element's update, once per write, which is once per animation frame here.
// The transform has already been applied by then, so the only damage is the
// uncaught error. Skip that call unless a session is actually presenting. If
// the renderer cannot be found the guard does nothing and the errors return.
const guarded = new WeakSet();
function guardArRenderer(mv) {
  try {
    let proto = Object.getPrototypeOf(mv);
    let renderer = null;
    while (proto && !renderer) {
      for (const sym of Object.getOwnPropertySymbols(proto)) {
        if (sym.description !== 'renderer') continue;
        const r = mv[sym];
        if (r && r.arRenderer) renderer = r;
      }
      proto = Object.getPrototypeOf(proto);
    }
    const ar = renderer && renderer.arRenderer;
    if (!ar || guarded.has(ar) || typeof ar.onUpdateScene !== 'function') return;
    const original = ar.onUpdateScene;
    ar.onUpdateScene = (...args) => (ar.isPresenting ? original.apply(ar, args) : undefined);
    guarded.add(ar);
  } catch (e) {
    // best effort; see above
  }
}

function parseOrbit(mv) {
  const { theta, phi, radius } = mv.getCameraOrbit();
  // getCameraOrbit returns radians for the angles and metres for the radius;
  // cameraOrbit is written back in degrees, so convert once here.
  return { theta: (theta * 180) / Math.PI, phi: (phi * 180) / Math.PI, radius };
}

export function createGestures(mv) {
  guardArRenderer(mv);
  // Property writes reach the element on a microtask; jumping before that
  // moves the camera to the PREVIOUS frame's goal and leaves the last frame to
  // ease in on the damper.
  const settleNow = () => mv.updateComplete.then(() => mv.jumpCameraToGoal());
  let flipAnim = null;
  let rotateAnim = null;
  let rollDeg = 0; // accumulated roll, like the fork's accumulated rotation.z

  const stop = () => {
    if (flipAnim !== null) cancelAnimationFrame(flipAnim);
    if (rotateAnim !== null) cancelAnimationFrame(rotateAnim);
    flipAnim = rotateAnim = null;
  };

  return {
    // Turn the card over: half a turn about the vertical axis.
    flip() {
      if (flipAnim !== null) return;
      const from = parseOrbit(mv);
      const start = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - start) / DURATION_MS);
        mv.cameraOrbit = `${from.theta + 180 * turnProfile(p)}deg ${from.phi}deg ${from.radius}m`;
        // Without this the element eases toward the goal on its own schedule and
        // the two animations fight; jumping each frame makes ours the only one.
        settleNow();
        flipAnim = p < 1 ? requestAnimationFrame(step) : null;
      };
      flipAnim = requestAnimationFrame(step);
    },

    // Quarter-turn the card in its own plane, counter-clockwise as seen from
    // the front, keeping the card's centre in the middle of the view the whole
    // way round (the fork moved the camera target to it every frame).
    rotate() {
      if (rotateAnim !== null) return;
      // The element may not have been upgraded yet when this was created (the
      // viewer is loaded on demand), so look for the renderer again here.
      guardArRenderer(mv);
      const from = rollDeg;
      const dir = Math.cos(mv.getCameraOrbit().theta) >= 0 ? 1 : -1;
      rollDeg += 90 * dir;
      // `orientation` turns the model about its own origin, which is not the
      // middle of the card, so the card's centre swings around that origin.
      // Follow it explicitly, as the fork did: an 'auto' target is a no-op
      // once it is already 'auto', and the element only re-centred well after
      // the turn, so the card visibly pivoted off-centre (BZL-212).
      const centre = mv.getBoundingBoxCenter();
      const start = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - start) / DURATION_MS);
        const turned = 90 * dir * turnProfile(p);
        mv.orientation = `${from + turned}deg 0deg 0deg`;
        // Roll is about the model's Z axis: rotate the starting centre with it.
        // toFixed, not String(): a near-zero cos/sin would print as 1e-17.
        const a = (turned * Math.PI) / 180;
        const x = centre.x * Math.cos(a) - centre.y * Math.sin(a);
        const y = centre.x * Math.sin(a) + centre.y * Math.cos(a);
        mv.cameraTarget = `${x.toFixed(6)}m ${y.toFixed(6)}m ${centre.z.toFixed(6)}m`;
        settleNow();
        rotateAnim = p < 1 ? requestAnimationFrame(step) : null;
      };
      rotateAnim = requestAnimationFrame(step);
    },

    // Called when the element is handed a different model: a new scan should
    // not inherit the previous one's quarter-turns.
    reset() {
      stop();
      rollDeg = 0;
      mv.orientation = '0deg 0deg 0deg';
      mv.cameraTarget = 'auto auto auto';
    },

    dispose: stop,
  };
}
